import React, { useState } from 'react';
import {
  Layout,
  SplitLoginLayout,
  Text,
  Button,
  Box,
  ExtInput,
  Checkbox,
} from '@flexisaf/flexibull2';
import PropTypes from 'prop-types';
import Theme from 'utils/theme';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router';
import { selectIsLoading } from 'redux/users/selectors';
import { userLoginStart } from 'redux/users/actions';
import BannerImage from 'assets/images/default-banner.svg';
import Logo from 'assets/images/Logo.svg';
import { selectOrgAliases } from 'redux/configurations/selectors';
import { LoginWrapper } from './style';
import { menuItems } from 'routes';
import { CanFilter } from 'components/CanView';
import { version } from 'version';
import ResetpasswordModal from 'containers/ResetPasswordModal';
import Google from 'assets/images/google.svg';
import orgConfig from 'utils/orgConfig';
import urls from 'utils/config';

const validationSchema = Yup.object().shape({
  username: Yup.string().required('Enter Username'),
  password: Yup.string().required('Enter Password'),
});

const LoginPage = ({ orgData }) => {
  const dispatch = useDispatch();
  const { params } = useRouteMatch();
  const history = useHistory();
  const [showPass, setShowPass] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const getAppId = localStorage.getItem('app_redirect');

  const aliases = useSelector(selectOrgAliases);
  const loading = useSelector(selectIsLoading);

  const { UMS_BASE_URL } = urls;

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: validationSchema,
    validateOnMount: false,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      dispatch(
        userLoginStart({ ...values, organisation: params.orgName }, (userData) => {
          const availableRoutes = CanFilter(menuItems(params.orgName, aliases));
          window.dataLayer.push({
            event: 'login',
            method: 'email',
            orgId: params.orgName,
            userId: userData?.id,
          });
          if (getAppId && availableRoutes.find((route) => route?.navlink === `/${params.orgName}/applicants`)) {
            history.push(
              `/${params.orgName}/applicants/?applicationId=${getAppId}`
            );
          } else {
            history.push(
              availableRoutes?.[0]?.navlink || `/${params.orgName}/user-profile`
            );
          }
        })
      );
    },
  });

  const onEnter = (e) => {
    e.stopPropagation();

    if (e.key === 'Enter') {
      formik.handleSubmit();
    }
  };

  function googleAuth() {
    const callback_url = `${window.location.href}/auth`;
    const orgId = params.orgName;
    const url = `${UMS_BASE_URL}/oauth2/google/authorize?callback_url=${callback_url}&organization=${orgId}&product=${orgConfig.product}`;
    window.location.href = url;
  }

  return (
    <div data-testid="login">
      <Layout theme={Theme}>
        <LoginWrapper>
          <SplitLoginLayout
            brand={<img src={Logo} alt="Logo" />}
            image={BannerImage}
            leftFoot={
              <Box>
                <Text>
                  Copyright &copy; {new Date().getFullYear()}{' '}
                  <strong>Safapply.</strong> All rights reserved.
                </Text>
              </Box>
            }
            rightTop={
              orgData && orgData?.logoUrl ? 
                <img src={`${orgData?.logoUrl}?${new Date().getTime()}`} alt="Logo" /> 
              : <Text>
                  Welcome to <strong>{orgData?.name}</strong>
                </Text>
            }
            rightFoot={
              <Box>
                <Text block>Version {version}</Text>
                <Box>
                  Powered by{' '}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="http://safapply.com/"
                  >
                    SAFApply
                  </a>{' '}
                  a product of{' '}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="http://flexisaf.com/"
                  >
                    Flexisaf
                  </a>
                </Box>
              </Box>
            }
          >
            <Box>
              <Button
                elevate="high"
                color="#fff"
                bold
                pale
                block
                fontColor="#333"
                height={46}
                iconLeft={<img src={Google} size="25px" alt="google" />}
                onClick={() => googleAuth()}
              >
                Login with Google
              </Button>
            </Box>
            <Box display="flex" margin="40px 0">
              <Box
                margin="8px 20px 0 auto"
                width="120px"
                height="1px"
                background="#ccc"
              />
              <Text bold>Or</Text>
              <Box
                margin="8px auto 0 20px"
                width="120px"
                height="1px"
                background="#ccc"
              />
            </Box>
            <Box>
              <Text block bold size="1.8rem">
                Sign In
              </Text>
              <Box height="1rem" />
              <Text>
                {`${params.orgName}`} Administrators Authorisation Page{' '}
              </Text>
              <ExtInput
                label="Email"
                name="username"
                id="username"
                onKeyPress={onEnter}
                value={formik.values.username}
                onChange={formik.handleChange}
                error={formik.errors.username}
                required
              />
              <Text
                color={({ theme }) => theme.PrimaryRed}
                style={{ fontStyle: 'italic' }}
              >
                {formik.errors.username}
              </Text>
              <ExtInput
                label="Password"
                name="password"
                id="password"
                onKeyPress={onEnter}
                type={showPass ? 'text' : 'password'}
                onChange={formik.handleChange}
                value={formik.values.password}
                error={formik.errors.password}
                required
              />
              <Text
                color={({ theme }) => theme.PrimaryRed}
                style={{ fontStyle: 'italic' }}
              >
                {formik.errors.password}
              </Text>
              <Checkbox
                checked={showPass}
                onChange={(e) => setShowPass(e.target.checked)}
                label="Show Password"
              />
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto max-content max-content',
                  gridGap: '10px',
                }}
                pad="20px 0 0 0"
              >
                <Box vAlign>
                  <Text>
                    Forgot your password?{' '}
                    <Text
                      className="reset-it"
                      bold
                      color={Theme.PrimaryColor}
                      onClick={() => setShowReset(true)}
                    >
                      Reset it
                    </Text>
                  </Text>
                </Box>
                <Button
                  height={46}
                  pad="0 0 0 35px"
                  block
                  progress={loading}
                  iconRight={<i className="saf-right-open" />}
                  onClick={formik.handleSubmit}
                >
                  Login
                </Button>
              </Box>
            </Box>
          </SplitLoginLayout>
        </LoginWrapper>
        <ResetpasswordModal isOpen={showReset} close={() => setShowReset(false)} />
      </Layout>
    </div>
  );
};

LoginPage.propTypes = {
  orgData: PropTypes.object
};

export default LoginPage;
